import { z } from "zod";
import { sanitizeSupportText } from "./support-request-context";
export { sanitizeSupportText } from "./support-request-context";

export const supportRequestCapabilitiesSchema = z.object({
  support_requests: z.boolean(),
});

/** 本轮已识别类型仅用于可编辑预填，发送时以顾客确认的选择为准。 */
export const supportRequestTypeSchema = z.enum([
  "order_status_tracking",
  "order_cancellation",
  "order_changes",
  "delivery_issues",
  "returns_exchanges_refunds",
  "product_issues_warranty",
  "product_subscriptions",
  "other_contact_support",
]);
export const supportRequestTypeLabels: Record<
  z.infer<typeof supportRequestTypeSchema>,
  string
> = {
  order_status_tracking: "Order status and tracking",
  order_cancellation: "Order cancellation",
  order_changes: "Order changes",
  delivery_issues: "Delivery issues",
  returns_exchanges_refunds: "Returns, exchanges and refunds",
  product_issues_warranty: "Product issues and warranty",
  product_subscriptions: "Product subscriptions",
  other_contact_support: "Other requests and contact support",
};

const reference = z.string().min(1).max(128);
export const supportRequestFieldsSchema = z.strictObject({
  request_type: supportRequestTypeSchema,
  contact_email: z.string().max(254),
  description: z
    .string()
    .max(1200)
    .transform(sanitizeSupportText)
    .pipe(z.string().max(1200)),
  order_reference: z.string().max(64),
});
export const supportRequestReadyFieldsSchema =
  supportRequestFieldsSchema.safeExtend({
    contact_email: z.email().max(254),
    description: supportRequestFieldsSchema.shape.description.refine(
      (value) => value.trim().length > 0,
    ),
  });
export const supportRequestEmailSchema = z.strictObject({
  from: z.email(),
  to: z.email(),
  replyTo: z.string().max(254),
  subject: z.string().max(256),
  sections: z
    .array(
      z.strictObject({
        title: z.string().max(128),
        lines: z.array(z.string().max(2000)).max(12),
      }),
    )
    .max(8),
});
export type SupportRequestEmail = z.infer<typeof supportRequestEmailSchema>;
export const supportRequestPayloadSchema =
  supportRequestFieldsSchema.safeExtend({
    request_id: reference,
    content_version: z.int().positive(),
    phase: z.enum(["draft", "paused"]),
    to: z.union([z.email().max(254), z.literal("")]),
    expires_at: z.iso.datetime(),
    email: supportRequestEmailSchema.optional(),
    accepted_at: z.iso.datetime().optional(),
    send_status: z
      .enum([
        "draft",
        "sending",
        "accepted",
        "delivered",
        "bounced",
        "failed",
        "unknown",
      ])
      .optional(),
  });
const envelope = z.strictObject({
  card_id: z.string().min(1).max(256),
  type: z.literal("support_request"),
  version: z.int().positive(),
  updated_at: z.iso.datetime(),
});
export const supportRequestCardSchema = z.discriminatedUnion("lifecycle", [
  envelope.extend({
    lifecycle: z.literal("active"),
    payload: supportRequestPayloadSchema,
  }),
  envelope.extend({
    lifecycle: z.enum(["expired", "revoked"]),
    payload: z.strictObject({ request_id: reference }),
  }),
]);
const { email: _preview, ...supportBlockShape } =
  supportRequestPayloadSchema.shape;
void _preview;
export const supportRequestBlockDataSchema = z.union([
  z.strictObject(supportBlockShape),
  z.strictObject({ request_id: reference, phase: z.literal("expired") }),
]);
export type SupportRequestBlockData = z.infer<
  typeof supportRequestBlockDataSchema
>;

export const supportRequestCommandSchema = z.discriminatedUnion("operation", [
  z.strictObject({
    operation: z.literal("open"),
    message_id: reference,
    ref: reference.optional(),
    /** 从订单卡打开时带上卡上的订单号，预填进订单号字段；属于顾客自报，不代表已核验。 */
    order_reference: supportRequestFieldsSchema.shape.order_reference
      .min(1)
      .optional(),
  }),
  z.strictObject({ operation: z.literal("read"), request_id: reference }),
  z.strictObject({
    operation: z.literal("save"),
    request_id: reference,
    content_version: z.int().positive(),
    fields: supportRequestFieldsSchema,
  }),
  z.strictObject({
    operation: z.literal("pause"),
    request_id: reference,
    content_version: z.int().positive(),
    fields: supportRequestFieldsSchema,
  }),
  z.strictObject({
    operation: z.literal("resume"),
    request_id: reference,
    content_version: z.int().positive(),
  }),
  z.strictObject({
    operation: z.literal("send"),
    request_id: reference,
    content_version: z.int().positive(),
  }),
  z.strictObject({
    operation: z.literal("check_send"),
    request_id: reference,
    content_version: z.int().positive(),
  }),
]);
export const supportRequestFailureSchema = z.object({
  error: z.enum([
    "invalid_fields",
    "not_accessible",
    "expired",
    "stale_version",
    "rate_limited",
    "support_email_unavailable",
    "support_email_not_configured",
    "upstream_unavailable",
  ]),
  fields: z.array(z.string()).optional(),
  card: supportRequestCardSchema.optional(),
});
export type SupportRequestFields = z.infer<typeof supportRequestFieldsSchema>;
export type SupportRequestCard = z.infer<typeof supportRequestCardSchema>;
export type SupportRequestCommand = z.infer<typeof supportRequestCommandSchema>;
export type SupportRequestFailure = z.infer<typeof supportRequestFailureSchema>;
